/* eslint-disable no-undef */
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import React from 'react';
import PropTypes from 'prop-types';
import AbstractButton from './AbstractButton';
import ConferenceSetting from '../ConferenceSetting';
import withBoundary from 'component/ErrorBoundary/withBoundary';
import { translate } from 'store/modules/base/i18n/functions';
import { connect } from 'store/modules/base/redux/functions';
import { MenuIcon } from 'assets/images';
import {
  registerShortcut,
  unregisterShortcut
} from 'store/modules/keyboard-shortcuts/actions';

/**
 * An abstract implementation of a button for accessing settings.
 */
class SettingsButton extends AbstractButton {
  accessibilityLabel = 'toolbar.accessibilityLabel.Settings';
  icon = MenuIcon;
  label = 'toolbar.Settings';
  type = 'white';

  // tooltip = 'toolbar.Settings';
  /**
   * Initializes a new {@code AudioSettingsButton} instance.
   *
   * @inheritdoc
   */
  constructor(props) {
    super(props);
    this._timeoutRef = React.createRef();

    // Bind event handlers so they are only bound once per instance.
    this._onKeyboardShortcut = this._onKeyboardShortcut.bind(this);
  }

  /**
   * Registers the keyboard shortcut that toggles the audio muting.
   *
   * @inheritdoc
   * @returns {void}
   */
  componentDidMount() {
    this.props.dispatch(
      registerShortcut({
        character: 'M',
        helpDescription: 'keyboardShortcuts.menu',
        handler: this._onKeyboardShortcut
      })
    );
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevState.isOpen && this.state.isOpen) {
      this._startTimer();
    }
    if (prevState.isOpen && !this.state.isOpen) {
      this._clearTimer();
    }
  }

  /**
   * Unregisters the keyboard shortcut that toggles the audio muting.
   *
   * @inheritdoc
   * @returns {void}
   */
  componentWillUnmount() {
    this.props.dispatch(unregisterShortcut('M'));
    this._clearTimer();
  }

  /**
   * Sets a 60 second timer to close the settings drawer.
   */
  _startTimer = () => {
    if (this._timeoutRef.current) clearTimeout(this._timeoutRef.current);
    this._timeoutRef.current = setTimeout(() => {
      this._clearTimer();
      super.setState({ isOpen: false });
    }, 60 * 1000);
  };

  /**
   * Clears the timer interval
   */
  _clearTimer = () => {
    if (this._timeoutRef.current) clearTimeout(this._timeoutRef.current);
    this._timeoutRef.current = null;
  };

  /**
   * Creates an analytics keyboard shortcut event and dispatches an action to
   * toggle the audio muting.
   *
   * @private
   * @returns {void}
   */
  _onKeyboardShortcut() {
    // Ignore keyboard shortcuts if the audio button is disabled.
    if (this._isDisabled()) {
      return;
    }

    AbstractButton.prototype._onClick.call(this);
  }

  /**
   * Handles clicking / pressing the button, and opens the appropriate dialog.
   *
   * @protected
   * @returns {void}
   */
  _handleClick() {
    //
  }

  _getDialogChildren() {
    return (
      <Box
        onClick={() => this._startTimer()}
        role="presentation"
        sx={{
          width: this.props.clientWidth < 500 ? 'auto' : 398,
          height: this.props.clientHeight
        }}>
        <List style={{ padding: '10px', height: '100%' }}>
          <ConferenceSetting
            open={this.state.isOpen}
            onClose={() => super.setState({ isOpen: false })}
          />
        </List>
      </Box>
    );
  }
}
SettingsButton.defaultProps = {
  customClass: 'white',
  showLabel: true,
  id: 'menu-button',
  styles: {
    iconStyle: {
      fill: '#222222'
    }
  }
};

function _mapStateToProps(state) {
  const { clientWidth, clientHeight } = state['features/base/responsive-ui'];

  return {
    clientWidth,
    clientHeight,
    _userIdleMode: state['features/settings'].userIdleMode
  };
}

/**
 * The type of the React {@code Component} props of {@link SettingsButton}.
 */
SettingsButton.propTypes = {
  /**
   * The default tab at which the settings dialog will be opened.
   */
  defaultTab: PropTypes.string,

  /**
   * The redux {@code dispatch} function.
   */
  dispatch: PropTypes.func,

  /**
   * Indicates whether the device selection dialog is displayed on the
   * welcome page or not.
   */
  isDisplayedOnWelcomePage: PropTypes.bool
};

export default React.memo(
  translate(connect(_mapStateToProps)(withBoundary(SettingsButton)))
);
